import { CrystalWithResultDTO } from 'business/models/Models';
import { FormInputRow } from 'components/input';
import React from 'react';
import { SynchrotronTripResultStatus } from '../constants';

import {
  getSynchTripResultInputFields,
  GetSynchTripResultsInputFieldsParams,
  SynchTripResultInputs,
} from './utils';

interface Props {
  crystal?: CrystalWithResultDTO;
  handleInputChange?: (e: any, id: number) => void;
  isReadOnly?: boolean;
}

const SynchrotronTripResultForm = (props: Props) => {
  const { crystal, handleInputChange, isReadOnly } = props;
  const synchTripResultsInputFieldsParams: GetSynchTripResultsInputFieldsParams = {
    crystal,
    handleInputChange: handleInputChange,
  };

  const renderCellFields = (f: SynchTripResultInputs) => {
    return (
      <div id={`tripresult-synchrotrontripresultform-cellfields-wrapper-${f.name}`}  className="mt-1 flex w-1/3 space-x-2">
        <label id={`tripresult-synchrotrontripresultform-${f.label}-label`} htmlFor={f.name} className="m-0 inline ml-1">
          {f.label}
        </label>
        <div id={`tripresult-synchrotrontripresultform-${f.name}-input-wrapper`} className="flex border border-dark rounded-sm overflow-hidden items-center">
          <input
            id={`tripresult-synchrotrontripresultform-${f.name}-input`}
            type={f.type}
            name={f.name}
            value={f.value}
            className="w-full border-0"
            onChange={(e) => handleInputChange(e, crystal?.id)}
            readOnly={isReadOnly}
          />
          <span id={`tripresult-synchrotrontripresultform-${f.name}-symbol`} className="bg-gray-200 w-6 text-center border">{f.symbol}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        id="tripresult-synchrotrontripresultform-crystalresult-wrapper"
        className="flex justify-between items-center"
      >
        <div
          id="tripresult-synchrotrontripresultform-crystalresult-title"
          className="font-bold"
        >
          Crystal Result
        </div>
        <div
          id="tripresult-synchrotrontripresultform-crystalresult-status"
          className="flex items-center space-x-3 my-1"
        >
          <div
            id="tripresult-synchrotrontripresultform-crystalresult-status-title"
            className="flex items-center gap-1"
          >
            Status
          </div>
          <div
            id="tripresult-synchrotrontripresultform-crystalresult-status-complete"
            className="flex items-center gap-1"
          >
            <input
              id="tripresult-synchrotrontripresultform-crystalresult-status-complete-input"
              type="radio"
              checked={
                crystal?.result?.resultStatus === SynchrotronTripResultStatus.Complete
              }
              value={2}
              onChange={(e) => handleInputChange(e, crystal?.id)}
              name="resultStatus"
              disabled={isReadOnly}
            />
            <label
              id="tripresult-synchrotrontripresultform-crystalresult-status-complete-label"
              htmlFor="complete"
              className="m-0"
            >
              Complete
            </label>
          </div>
          <div
            id="tripresult-synchrotrontripresultform-crystalresult-status-partial"
            className="flex items-center gap-1"
          >
            <input
              id="tripresult-synchrotrontripresultform-crystalresult-status-partial-input"
              type="radio"
              checked={
                crystal?.result?.resultStatus === SynchrotronTripResultStatus.Partial
              }
              value={1}
              onChange={(e) => handleInputChange(e, crystal?.id)}
              name="resultStatus"
              disabled={isReadOnly}
            />
            <label
              id="tripresult-synchrotrontripresultform-crystalresult-status-partial-label"
              htmlFor="partial"
              className="m-0"
            >
              Partial
            </label>
          </div>
          <div
            id="tripresult-synchrotrontripresultform-crystalresult-status-noresult"
            className="flex items-center gap-1"
          >
            <input
              id="tripresult-synchrotrontripresultform-crystalresult-status-noresult-input"
              type="radio"
              checked={
                crystal?.result?.resultStatus === SynchrotronTripResultStatus.NoResult ||
                !crystal?.result?.resultStatus
              }
              value={0}
              onChange={(e) => handleInputChange(e, crystal?.id)}
              name="resultStatus"
              disabled={isReadOnly}
            />
            <label
              id="tripresult-synchrotrontripresultform-crystalresult-status-noresult-label"
              htmlFor="noResult"
              className="m-0"
            >
              No Result
            </label>
          </div>
        </div>
      </div>
      <div
        id="tripresult-synchrotrontripresultform-inputfields-wrapper"
        className="mt-2 flex gap-3"
      >
        <div
          id="tripresult-synchrotrontripresultform-inputfields-container"
          className="w-1/2 flex flex-col gap-1.5"
        >
          {getSynchTripResultInputFields('A', synchTripResultsInputFieldsParams).map(
            (f, index) => (
              <FormInputRow
                key={index}
                id={f.name}
                type={f.type}
                name={f.name}
                labeltext={f.label}
                value={f.value}
                onChange={(e) => f.onChange(e, crystal?.id)}
                disabled={f.isDisabled}
                readOnly={isReadOnly}
              />
            ),
          )}
          <div
            id="tripresult-synchrotrontripresultform-inputfields-reso-lo/hi-shells-wrapper"
            className="flex items-center"
          >
            <label
              id="tripresult-synchrotrontripresultform-inputfields-reso-lo/hi-shells-label"
              htmlFor="resoLow"
              className="m-0 w-[30%]"
            >
              Reso_lo/hi_shells
            </label>
            <div
              id="tripresult-synchrotrontripresultform-inputfields-reso-lo/hi-shells-input-lists"
              className="flex justify-between w-[70%] gap-2"
            >
              {getSynchTripResultInputFields('B', synchTripResultsInputFieldsParams).map(
                (f) => (
                  <>
                    <input
                      id={f.name}
                      type={f.type}
                      name={f.name}
                      value={f.value}
                      className="w-1/2"
                      onChange={(e) => handleInputChange(e, crystal?.id)}
                      readOnly={isReadOnly}
                    />
                  </>
                ),
              )}
            </div>
          </div>
          <div
            id="tripresult-synchrotrontripresultform-inputfields-cell-wrapper"
            className="flex items-center"
          >
            <label
              id="tripresult-synchrotrontripresultform-inputfields-cell-label"
              htmlFor="cellA"
              className="m-0 w-[30%]"
            >
              cell
            </label>
            <div
              id="tripresult-synchrotrontripresultform-inputfields-cell-input"
              className="flex justify-between w-[70%]"
            >
              <div
                id="tripresult-synchrotrontripresultform-inputfields-cell-input-lists"
                className="flex flex-wrap items-center w-full"
              >
                {getSynchTripResultInputFields(
                  'C',
                  synchTripResultsInputFieldsParams,
                ).map((f) => renderCellFields(f))}
              </div>
            </div>
          </div>
          {getSynchTripResultInputFields('D', synchTripResultsInputFieldsParams).map(
            (f, index) => (
              <FormInputRow
                key={index}
                id={f.name}
                type={f.type}
                name={f.name}
                labeltext={f.label}
                value={f.value}
                onChange={(e) => f.onChange(e, crystal?.id)}
                disabled={f.isDisabled}
                readOnly={isReadOnly}
              />
            ),
          )}
        </div>
        <div
          id="tripresult-synchrotrontripresultform-inputfields-sub-container"
          className="w-1/2 flex flex-col gap-1.5"
        >
          {getSynchTripResultInputFields('E', synchTripResultsInputFieldsParams).map(
            (f, index) => (
              <FormInputRow
                key={index}
                id={f.name}
                type={f.type}
                name={f.name}
                labeltext={f.label}
                value={f.value}
                onChange={(e) => f.onChange(e, crystal?.id)}
                disabled={f.isDisabled}
                readOnly={isReadOnly}
              />
            ),
          )}
          <div
            id="tripresult-synchrotrontripresultform-inputfields-notes-wrapper"
            className="flex h-full"
          >
            <label
              id="tripresult-synchrotrontripresultform-inputfields-notes-label"
              htmlFor="resultNotes"
              className="m-0 w-[30%]"
            >
              Notes
            </label>
            <textarea
              id="tripresult-synchrotrontripresultform-inputfields-notes-result-text"
              name="resultNotes"
              rows={5}
              value={crystal?.result?.resultNotes || ''}
              className="w-[70%] h-full select-text selection:bg-blue-500 selection:text-white"
              onChange={(e) => handleInputChange(e, crystal?.id)}
              readOnly={isReadOnly}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SynchrotronTripResultForm;
